import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Video, Clock, CheckCircle, XCircle, Loader } from 'lucide-react';
import api from '../../shared/services/api';

interface Video {
  id: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  durationSeconds?: number;
  status: 'Uploading' | 'Processing' | 'Processed' | 'Failed';
  createdAt: string;
  qualities: Array<{
    resolution: string;
    url: string;
    fileSizeBytes: number;
  }>;
}

export function AdminVideosPage() {
  const { data: videos, isLoading } = useQuery<Video[]>({
    queryKey: ['admin-videos'],
    queryFn: async () => {
      const response = await api.get('/Videos');
      return response.data;
    },
  });

  const videosMock: Video[] = [
        {
          id: '1',
          title: 'Introdução ao React',
          description: 'Aprenda os fundamentos do React',
          thumbnailUrl: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400',
          durationSeconds: 1245,
          status: 'Processed',
          createdAt: '2026-01-03T10:00:00Z',
          qualities: [
            { resolution: '1080p', url: '/videos/react-intro-1080p.mp4', fileSizeBytes: 104857600 },
            { resolution: '720p', url: '/videos/react-intro-720p.mp4', fileSizeBytes: 52428800 },
            { resolution: '480p', url: '/videos/react-intro-480p.mp4', fileSizeBytes: 26214400 },
          ],
        },
        {
          id: '2',
          title: 'TypeScript Avançado',
          description: 'Conceitos avançados de TypeScript',
          thumbnailUrl: 'https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=400',
          durationSeconds: 2340,
          status: 'Processing',
          createdAt: '2026-01-03T11:30:00Z',
          qualities: [],
        },
        {
          id: '3',
          title: 'Node.js e Express',
          description: 'Construindo APIs com Node.js',
          thumbnailUrl: 'https://images.unsplash.com/photo-1627398242454-45a1465c2479?w=400',
          durationSeconds: 1890,
          status: 'Processed',
          createdAt: '2026-01-03T09:15:00Z',
          qualities: [
            { resolution: '1080p', url: '/videos/nodejs-1080p.mp4', fileSizeBytes: 94371840 },
            { resolution: '720p', url: '/videos/nodejs-720p.mp4', fileSizeBytes: 47185920 },
            { resolution: '480p', url: '/videos/nodejs-480p.mp4', fileSizeBytes: 23592960 },
          ],
        },
      ];

  // Se não houver vídeos da API, usar dados de exemplo
  const displayVideos = videos && videos.length > 0 ? videos : videosMock;

  const getStatusBadge = (status: Video['status']) => {
    switch (status) {
      case 'Processed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Processado
          </span>
        );
      case 'Processing':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
            <Loader className="w-3 h-3 animate-spin" />
            Processando
          </span>
        );
      case 'Uploading':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
            <Loader className="w-3 h-3 animate-spin" />
            Enviando
          </span>
        );
      case 'Failed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Erro
          </span>
        );
    }
  };

  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    
    if (h > 0) {
      return `${h}h ${m}m`;
    }
    return `${m}m ${s}s`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Gerenciar Vídeos</h1>
              <p className="text-sm text-gray-600">
                {displayVideos?.length || 0} vídeo{displayVideos?.length !== 1 ? 's' : ''}
              </p>
            </div>
            <Link
              to="/admin/videos/upload"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Novo Vídeo
            </Link>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : displayVideos && displayVideos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayVideos.map((video) => (
              <div
                key={video.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Thumbnail */}
                <div className="relative aspect-video bg-gray-900">
                  {video.thumbnailUrl ? (
                    <img
                      src={video.thumbnailUrl}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full">
                      <Video className="w-16 h-16 text-gray-600" />
                    </div>
                  )}
                  
                  {video.durationSeconds && (
                    <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 text-white text-xs rounded flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDuration(video.durationSeconds)}
                    </div>
                  )}
                </div>

                {/* Informações */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-gray-900 line-clamp-2">
                      {video.title}
                    </h3>
                    {getStatusBadge(video.status)}
                  </div>

                  {video.description && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {video.description}
                    </p>
                  )}

                  {/* Qualidades disponíveis */}
                  {video.qualities.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Qualidades disponíveis:</p>
                      <div className="flex flex-wrap gap-1">
                        {video.qualities.map((quality) => (
                          <span
                            key={quality.resolution}
                            className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded"
                          >
                            {quality.resolution} ({formatFileSize(quality.fileSizeBytes)})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-gray-500 border-t border-gray-100 pt-3">
                    <span>
                      {new Date(video.createdAt).toLocaleDateString('pt-BR')}
                    </span>
                    <Link
                      to={`/admin/videos/${video.id}`}
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Detalhes
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhum vídeo cadastrado
            </h3>
            <p className="text-gray-600 mb-6">
              Comece fazendo upload do seu primeiro vídeo
            </p>
            <Link
              to="/admin/videos/upload"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Enviar Vídeo
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
